import CryptoJS from 'crypto-js';
import jwtDecode from 'jwt-decode';
import { appConfig } from '../config';
import { mobileDevice } from '../utils/constants/browser';

export type SocialProvider = 'google' | 'apple' | 'facebook';

const CALLBACK_PATH = '/account/callback';

const STORAGE_KEYS = {
    CODE_VERIFIER: 'pkce_code_verifier',
    STATE: 'pkce_state',
    PROVIDER: 'social_provider',
    LAST_PROVIDER: 'social_last_provider',
} as const;

function generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(36).padStart(2, '0'))
        .join('')
        .slice(0, length);
}

function generateCodeChallenge(verifier: string): string {
    const hash = CryptoJS.SHA256(verifier);
    return hash.toString(CryptoJS.enc.Base64)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

export interface JwtClaims {
    email?: string;
    preferred_username?: string;
    sub?: string;
    exp?: number;
    iat?: number;
    iss?: string;
    azp?: string;
    given_name?: string;
    family_name?: string;
    [key: string]: unknown;
}

export function decodeJwtPayload(token: string): JwtClaims | null {
    try {
        return jwtDecode<JwtClaims>(token);
    } catch {
        return null;
    }
}

export function getEmailFromToken(token: string): string | null {
    const claims = decodeJwtPayload(token);
    if (!claims) return null;
    // Apple IDP puts email in preferred_username instead of email
    return claims.email || claims.preferred_username || null;
}

const POPUP_NAME = 'social-login';

// Apple's consent page is wider than Google/Facebook's.
const POPUP_SIZES: Record<SocialProvider, { width: number; height: number }> = {
    apple: { width: 700, height: 700 },
    google: { width: 500, height: 700 },
    facebook: { width: 500, height: 700 },
};

function buildPopupFeatures(provider: SocialProvider): string {
    const { width, height } = POPUP_SIZES[provider];
    // Centre the popup on the opener's current screen (dual-monitor safe).
    const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
    const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
    const outerWidth = window.outerWidth || document.documentElement.clientWidth || window.screen.width;
    const outerHeight = window.outerHeight || document.documentElement.clientHeight || window.screen.height;
    const left = Math.max(0, Math.floor(dualScreenLeft + (outerWidth - width) / 2));
    const top = Math.max(0, Math.floor(dualScreenTop + (outerHeight - height) / 2));
    return `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
}
export const SOCIAL_LOGIN_MESSAGE_TYPE = 'social-login-callback';

export class SocialLoginCancelled extends Error {
    constructor() {
        super('Social login cancelled');
        this.name = 'SocialLoginCancelled';
    }
}

// Thrown when the provider (or Keycloak) signals a user-initiated cancel via an
// OIDC error code (access_denied / login_required) — distinct from the user X-ing
// the popup, which stays silent. Lets callers pick the right UX via instanceof.
export class SocialLoginProviderCancelled extends Error {
    constructor(message?: string) {
        super(message || 'Social login cancelled at provider');
        this.name = 'SocialLoginProviderCancelled';
    }
}

function isProviderCancelCode(code: string | undefined): boolean {
    return code === 'access_denied' || code === 'login_required';
}

export interface SocialLoginCallbackMessage {
    type: typeof SOCIAL_LOGIN_MESSAGE_TYPE;
    code?: string;
    state?: string;
    error?: string;
    errorDescription?: string;
}

export const SOCIAL_LOGIN_ERROR_MESSAGE_TYPE = 'social-login-error';

export interface SocialLoginErrorMessage {
    type: typeof SOCIAL_LOGIN_ERROR_MESSAGE_TYPE;
    error?: string;
}

function buildAuthUrl(provider: SocialProvider, callbackPath?: string): string {
    const codeVerifier = generateRandomString(64);
    const codeChallenge = generateCodeChallenge(codeVerifier);
    const state = generateRandomString(32);

    localStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
    localStorage.setItem(STORAGE_KEYS.STATE, state);
    localStorage.setItem(STORAGE_KEYS.PROVIDER, provider);

    const redirectUri = `${window.location.origin}${callbackPath || CALLBACK_PATH}`;
    const lastProvider = localStorage.getItem(STORAGE_KEYS.LAST_PROVIDER);

    const params = new URLSearchParams({
        client_id: appConfig.KEYCLOAK_CLIENT_ID,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid profile email',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state,
        kc_idp_hint: provider,
    });

    // Force re-authentication when switching to a different provider
    if (lastProvider && lastProvider !== provider) {
        params.set('prompt', 'login');
    }

    return `${appConfig.KEYCLOAK_URL}/realms/${appConfig.KEYCLOAK_REALM}/protocol/openid-connect/auth?${params.toString()}`;
}

// Opens a popup for the social login flow. Returns the popup handle on success,
// or null if the popup was blocked or we are on a mobile device — in those cases
// the function falls back to a full-page redirect and the calling page will unload.
export function startSocialLogin(provider: SocialProvider, callbackPath?: string): Window | null {
    const authUrl = buildAuthUrl(provider, callbackPath);

    // Mobile browsers (especially iOS Safari) open popups as new tabs and often
    // sever the opener relationship, breaking postMessage + window.close. Skip
    // the popup attempt entirely and use full-page redirect on mobile.
    if (mobileDevice) {
        window.location.href = authUrl;
        return null;
    }

    let popup: Window | null = null;
    try {
        popup = window.open(authUrl, POPUP_NAME, buildPopupFeatures(provider));
    } catch {
        popup = null;
    }

    if (!popup || popup.closed) {
        window.location.href = authUrl;
        return null;
    }

    try {
        popup.focus();
    } catch {
        // ignore — some browsers throw on focus
    }

    return popup;
}

// Listens for a postMessage from the social login popup and polls the popup's
// `closed` state so we can detect user-initiated cancellation. Note: Cross-Origin-
// Opener-Policy blocks `popup.closed` access while the popup is on a cross-origin
// document (Keycloak / Apple / Google / Facebook), so Chrome logs a warning each
// poll tick. That's expected — the poll still resolves correctly once the popup
// returns to our origin or is fully closed.
export function listenForSocialLoginCallback(popup: Window): Promise<{ code: string; state: string }> {
    return new Promise((resolve, reject) => {
        const ALLOWED_ORIGINS: string[] = [window.location.origin];
        try {
            ALLOWED_ORIGINS.push(new URL(appConfig.KEYCLOAK_URL).origin);
        } catch (err) {
            // Config missing/malformed — surface a warning so ops can see the
            // allowlist silently collapsed to same-origin only.
            console.warn('[social-login] invalid KEYCLOAK_URL; postMessage allowlist limited to same-origin', appConfig.KEYCLOAK_URL, err);
        }

        let settled = false;
        let pollTimer: ReturnType<typeof setInterval> | null = null;

        const cleanup = () => {
            window.removeEventListener('message', handleMessage);
            if (pollTimer !== null) {
                clearInterval(pollTimer);
                pollTimer = null;
            }
            try {
                if (!popup.closed) popup.close();
            } catch {
                // ignore — COOP may block, popup may already be closed
            }
        };

        const finish = (fn: () => void) => {
            if (settled) return;
            settled = true;
            cleanup();
            fn();
        };

        function handleMessage(event: MessageEvent) {
            if (!ALLOWED_ORIGINS.includes(event.origin)) return;
            // Reference equality on the Window handle from window.open — not a
            // cross-origin property access, so COOP doesn't block it. Rejects
            // forged messages from other same-origin windows/iframes/extensions.
            if (event.source !== popup) return;
            const data = event.data as SocialLoginCallbackMessage | SocialLoginErrorMessage | undefined;
            if (!data) return;

            if (data.type === SOCIAL_LOGIN_ERROR_MESSAGE_TYPE) {
                const error = data.error || 'social_login_failed';
                finish(() => reject(
                    isProviderCancelCode(error)
                        ? new SocialLoginProviderCancelled(error)
                        : new Error(error)
                ));
                return;
            }
            if (data.type !== SOCIAL_LOGIN_MESSAGE_TYPE) return;

            if (data.error) {
                const message = data.errorDescription || data.error || 'Social login failed';
                finish(() => reject(
                    isProviderCancelCode(data.error)
                        ? new SocialLoginProviderCancelled(message)
                        : new Error(message)
                ));
                return;
            }

            const { code, state } = data;
            if (!code || !state) {
                finish(() => reject(new Error('Missing code or state in social login callback')));
                return;
            }

            finish(() => resolve({ code, state }));
        }

        window.addEventListener('message', handleMessage);

        pollTimer = setInterval(() => {
            let closed = false;
            try {
                closed = popup.closed;
            } catch {
                // COOP may throw on access — treat as still open
                closed = false;
            }
            if (closed) {
                finish(() => reject(new SocialLoginCancelled()));
            }
        }, 1500);
    });
}

export interface KeycloakTokenResponse {
    access_token: string;
    refresh_token: string;
    id_token: string;
    expires_in: number;
    refresh_expires_in: number;
    token_type: string;
}

export async function exchangeCodeForTokens(code: string, callbackPath?: string): Promise<KeycloakTokenResponse> {
    const codeVerifier = localStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
    if (!codeVerifier) {
        throw new Error('Missing PKCE code verifier');
    }

    const redirectUri = `${window.location.origin}${callbackPath || CALLBACK_PATH}`;

    const body = new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri,
        client_id: appConfig.KEYCLOAK_CLIENT_ID,
        code_verifier: codeVerifier,
    });

    const tokenUrl = `${appConfig.KEYCLOAK_URL}/realms/${appConfig.KEYCLOAK_REALM}/protocol/openid-connect/token`;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);

    try {
        const response = await fetch(tokenUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
            signal: controller.signal,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error_description || error.error || 'Token exchange failed');
        }

        let data: KeycloakTokenResponse
        try {
            data = await response.json()
        } catch {
            throw new Error(`Token exchange returned non-JSON response (HTTP ${response.status})`)
        }

        if (!data.access_token) {
            throw new Error('Token exchange returned no access token');
        }

        return data;
    } finally {
        clearTimeout(timeout);
    }
}

export function validateCallbackState(stateFromUrl: string | null): boolean {
    const storedState = localStorage.getItem(STORAGE_KEYS.STATE);
    return !!stateFromUrl && stateFromUrl === storedState;
}

export function clearPkceStorage(): void {
    localStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
    localStorage.removeItem(STORAGE_KEYS.STATE);
}

const VALID_PROVIDERS: ReadonlySet<string> = new Set(['google', 'apple', 'facebook']);

function toProvider(value: string | null): SocialProvider | null {
    return value && VALID_PROVIDERS.has(value) ? (value as SocialProvider) : null;
}

export function getSocialProvider(): SocialProvider | null {
    return toProvider(localStorage.getItem(STORAGE_KEYS.PROVIDER));
}

export function clearSocialProvider(): void {
    localStorage.removeItem(STORAGE_KEYS.PROVIDER);
}

export function clearSocialLoginSession(): void {
    clearPkceStorage();
    clearSocialProvider();
}

export function setLastSocialProvider(provider: SocialProvider): void {
    localStorage.setItem(STORAGE_KEYS.LAST_PROVIDER, provider);
}

export function getLastSocialProvider(): SocialProvider | null {
    return toProvider(localStorage.getItem(STORAGE_KEYS.LAST_PROVIDER));
}
